import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getDashboard, getVentesMensuelles, getTopStations, getAlertesRecentes } from '../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const formatMontant = (n: number) => new Intl.NumberFormat('fr-FR').format(Math.round(n)) + ' FCFA';
const formatVolume = (n: number) => new Intl.NumberFormat('fr-FR').format(Math.round(n)) + ' L';
const GLOBAL_ROLES = ['superadmin', 'admin_siege', 'comptable', 'financier', 'fiscaliste', 'auditeur'];

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<any>(null);
  const [ventes, setVentes] = useState<any[]>([]);
  const [topStations, setTopStations] = useState<any[]>([]);
  const [alertes, setAlertes] = useState<any[]>([]);
  const [annee] = useState(new Date().getFullYear());

  const isGlobal = GLOBAL_ROLES.includes(user?.role || '');

  useEffect(() => {
    getDashboard().then(r => setStats(r.data)).catch(console.error);
    getVentesMensuelles(annee).then(r => setVentes(r.data)).catch(console.error);
    if (isGlobal) {
      getTopStations().then(r => setTopStations(r.data)).catch(console.error);
    }
    getAlertesRecentes().then(r => setAlertes(r.data)).catch(console.error);
  }, [annee]);

  if (!stats) return <div className="loading">Chargement...</div>;

  return (
    <div>
      <h2>Tableau de bord</h2>

      <div className="stats-grid">
        {isGlobal && (
          <div className="stat-card">
            <div className="stat-value">{stats.stations}</div>
            <div className="stat-label">Stations actives</div>
          </div>
        )}
        <div className="stat-card accent">
          <div className="stat-value">{formatMontant(stats.caMois)}</div>
          <div className="stat-label">CA du mois</div>
          {stats.tendanceCA !== 0 && (
            <div className={`stat-trend ${stats.tendanceCA > 0 ? 'up' : 'down'}`}>
              {stats.tendanceCA > 0 ? '+' : ''}{stats.tendanceCA}%
            </div>
          )}
        </div>
        <div className="stat-card">
          <div className="stat-value">{formatVolume(stats.volumeMois)}</div>
          <div className="stat-label">Volume du mois</div>
        </div>
        <div className="stat-card warning">
          <div className="stat-value">{stats.alertes}</div>
          <div className="stat-label">Alertes actives</div>
        </div>
      </div>

      <div className="grid-2">
        <div className="card">
          <h3>Ventes mensuelles {annee}</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={ventes}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="mois" />
              <YAxis tickFormatter={(v: any) => `${Math.round(v / 1000000)}M`} />
              <Tooltip formatter={(v: any) => formatMontant(v)} />
              <Legend />
              <Bar dataKey="ca" name="Chiffre d'affaires" fill="#ED0000" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {isGlobal ? (
          <div className="card">
            <h3>Top stations du mois</h3>
            <table className="table">
              <thead>
                <tr><th>Station</th><th>Ville</th><th style={{textAlign:'right'}}>CA</th><th style={{textAlign:'right'}}>Volume</th></tr>
              </thead>
              <tbody>
                {topStations.slice(0, 8).map((s: any) => (
                  <tr key={s.id}>
                    <td><strong>{s.nom}</strong></td>
                    <td>{s.ville}</td>
                    <td style={{textAlign:'right'}}>{formatMontant(s.ca)}</td>
                    <td style={{textAlign:'right'}}>{formatVolume(s.volume)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="card">
            <h3>Alertes actives</h3>
            {alertes.length === 0 ? (
              <p style={{ color: '#78BE20', fontWeight: 600, textAlign: 'center', padding: 30 }}>Aucune alerte active</p>
            ) : (
              <table className="table">
                <thead>
                  <tr><th>Type</th><th>Titre</th><th>Severite</th><th>Date</th></tr>
                </thead>
                <tbody>
                  {alertes.map((a: any) => (
                    <tr key={a.id}>
                      <td>{a.type_alerte}</td>
                      <td>{a.titre}</td>
                      <td><span className={`badge badge-${a.severite}`}>{a.severite}</span></td>
                      <td>{new Date(a.created_at).toLocaleDateString('fr-FR')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {isGlobal && (
        <div className="card" style={{ marginTop: 20 }}>
          <h3>Alertes recentes</h3>
          {alertes.length === 0 ? (
            <p style={{ color: '#999' }}>Aucune alerte active</p>
          ) : (
            <table className="table">
              <thead>
                <tr><th>Station</th><th>Type</th><th>Titre</th><th>Severite</th><th>Date</th></tr>
              </thead>
              <tbody>
                {alertes.map((a: any) => (
                  <tr key={a.id}>
                    <td>{a.station_nom}</td>
                    <td>{a.type_alerte}</td>
                    <td>{a.titre}</td>
                    <td><span className={`badge badge-${a.severite}`}>{a.severite}</span></td>
                    <td>{new Date(a.created_at).toLocaleDateString('fr-FR')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
